import React, { useEffect, useState } from "react";

import { getResiduePayoutPotential } from "services";

import {
  filterOptionsWithSynonyms,
  formatCurrency,
  getLocalStorageCropExplorerPseudoUserId,
  useAddCropExplorationEntry,
  useDeleteCropExplorationEntry,
  useTranslation,
  useUpdateCropExplorationEntry,
} from "utils";

import {
  CROP_CREATION_MODAL_TYPE,
  CROP_TYPE,
  CropExplorationResponse,
  CropExplorationTemplateResponse,
  CropExplorerCrop,
  ExtendedCrop,
  PracticeCrop,
  WithInjectedName,
} from "models";

import { AlertBanner, Button, Divider, Flex, Form, Icon, OptGroup, Option, Radio, RadioChangeEvent, Select } from "components";

import { useCropExplorationContext } from "pages/app/crop-explorer/CropExplorationContext";

export type CropCreationFormProps = {
  cardId: string;
  crop?: CropExplorerCrop;
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse;
  cropType: CROP_TYPE;
  dataTestId?: string;
  type: CROP_CREATION_MODAL_TYPE;
  year: number;
  cropResiduePayout: number;
  afterAddOrUpdate: () => void;
  onClose: () => void;
  isAdmin?: boolean;
};

export const CropCreationForm = ({
  dataTestId,
  cardId,
  cropType,
  crop,
  cropExploration,
  year,
  type,
  cropResiduePayout,
  afterAddOrUpdate,
  onClose,
  isAdmin,
}: CropCreationFormProps) => {
  const { t } = useTranslation(["cropExplorer", "modules"]);
  const { availableCrops } = useCropExplorationContext();
  const [selectedCropId, setSelectedCropId] = useState<string | undefined>(crop?.cropId);
  const [hasResidue, setHasResidue] = useState<boolean>(crop?.hasResidue || false);
  const [selectionResiduePayout, setSelectionResiduePayout] = useState<number>(cropResiduePayout);

  const [form] = Form.useForm();
  const pseudoUserId = getLocalStorageCropExplorerPseudoUserId();
  const isBauYear = cropExploration.bauYears.includes(year);

  const { mutate: addCropExplorationEntry, isPending: isPendingAddCropExplorationEntry, isSuccess: isAddSuccess } = useAddCropExplorationEntry(!!isAdmin);
  const {
    mutate: updateCropExplorationEntry,
    isPending: isPendingUpdateCropExplorationEntry,
    isSuccess: isUpdateSuccess,
  } = useUpdateCropExplorationEntry(!!isAdmin);
  const { mutate: deleteCropExplorationEntry, isPending: isDeletionPending, isSuccess: isDeletionSuccess } = useDeleteCropExplorationEntry(!!isAdmin);

  const handleFinish = (formData: CropExplorerCrop) => {
    if (type === CROP_CREATION_MODAL_TYPE.ADD) {
      addCropExplorationEntry({
        cropExplorationId: cropExploration.id,
        crop: { cropId: formData.cropId, year: year, residue: hasResidue, type: cropType },
        pseudoUserId,
      });
      return;
    }
    if (!crop) return;
    updateCropExplorationEntry({
      cropExplorationId: cropExploration.id,
      entryId: crop.id,
      crop: { cropId: formData.cropId, year: year, residue: formData.hasResidue ?? false, type: cropType },
      pseudoUserId,
    });
  };

  const onFinishFailedFailed = () => {};

  const handleResidueChange = (e: RadioChangeEvent) => {
    setHasResidue(e.target.value);
  };

  const handleDelete = (id: string) => {
    deleteCropExplorationEntry({ id, pseudoUserId });
  };

  useEffect(() => {
    if (isAddSuccess || isUpdateSuccess || isDeletionSuccess) afterAddOrUpdate();
  }, [isAddSuccess, isUpdateSuccess, isDeletionSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedCropId || !cropExploration || selectedCropId === crop?.cropId || isAdmin) {
      return;
    }

    getResiduePayoutPotential({
      cropId: selectedCropId,
      hectares: (cropExploration as CropExplorationResponse).hectares,
      regionId: (cropExploration as CropExplorationResponse).regionId,
    }).then((res) => {
      setSelectionResiduePayout(res.residuePayoutAvg as number);
    });
  }, [crop, cropExploration, isAdmin, selectedCropId]);

  const showResidue = availableCrops.find((crop: PracticeCrop) => crop.id === selectedCropId)?.residue;

  return (
    <Form
      data-testid="crop-creation-form"
      className="crop-creation-form"
      name={cardId}
      onFinish={handleFinish}
      onFinishFailed={onFinishFailedFailed}
      form={form}
    >
      <Flex className="padding-bottom-200" justify="space-between" gap="1rem">
        <h5 data-testid="crop-creation-form-title">{t(`addEditCrop.${type}Title`)}</h5>
        <Icon dataTestId="crop-creation-form-close-icon" className="pointer" iconName="tabler/x" onClick={onClose} />
      </Flex>
      <Form.Item name="cropExplorationId" hidden={true} initialValue={cropExploration.id}>
        <div>{cropExploration.id}</div>
      </Form.Item>
      <div className="full-width">
        {/* @ts-expect-error need to convert to tsx */}
        <Select
          dataTestId="crop-creation-form-crop-selection"
          label={t(`cropExplorer:cropCard.${cropType.toLowerCase()}`)}
          name="cropId"
          initialValue={availableCrops.find((availableCrop) => availableCrop.id === selectedCropId) ? selectedCropId : null}
          value={selectedCropId}
          onChange={(value) => setSelectedCropId(value)}
          rules={[{ required: true, message: false }]}
          showSearch
          optionFilterProp="children"
          filterOption={filterOptionsWithSynonyms}
        >
          {generateCropSelectionGroups({
            cropExploration: cropExploration,
            selectedCropId: selectedCropId,
            cropType: cropType,
            crops: availableCrops,
            year: year,
            t: t,
            isAdmin: !!isAdmin,
          })}
        </Select>
      </div>
      {showResidue ? (
        <>
          <Divider className="margin-top-200 margin-bottom-100" />
          <p data-testid="crop-creation-form-residue-form-item-label" className="residue-label">
            {t("cropExplorer:addEditCrop.residueLeft")}
          </p>
          {!isBauYear && selectionResiduePayout !== 0 && (
            <AlertBanner
              className="margin-bottom-150"
              description={selectionResiduePayout > 0 ? t("cropExplorer:addEditCrop.leaveResidueInfo") : t("cropExplorer:addEditCrop.removeResidueInfo")}
              type="info"
              iconName="tabler/bulb"
            />
          )}
          <Form.Item
            data-testid="crop-creation-form-residue-form-item"
            className="padding-bottom-200"
            name="hasResidue"
            initialValue={crop ? crop.hasResidue : undefined}
            rules={[{ required: true, message: t("cropExplorer:addEditCrop.selectionRequiredErrorMessage") }]}
          >
            <Radio.Group
              data-testid="crop-creation-form-residue-form-item-radio-group"
              className="crop-explorer"
              options={[
                {
                  value: true,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("cropExplorer:addEditCrop.yes")}</span>
                      {!isBauYear && typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && (
                        <span className="paragraph-s grey-text" data-testid="crop-creation-form-residue-form-payout">
                          {t("cropExplorer:addEditCrop.avgPayout")} {formatCurrency(selectionResiduePayout, 0, "always")}
                        </span>
                      )}
                    </Flex>
                  ),
                },
                {
                  value: false,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("cropExplorer:addEditCrop.no")}</span>
                      {!isBauYear && typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && (
                        <span className="paragraph-s grey-text" data-testid="crop-creation-form-residue-form-comparison">
                          {formatCurrency(0, 0)}
                        </span>
                      )}
                    </Flex>
                  ),
                },
              ]}
              onChange={handleResidueChange}
              value={hasResidue}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
        </>
      ) : (
        <div className="margin-bottom-100"></div>
      )}
      {crop ? (
        <Flex className="pointer padding-bottom-150" onClick={() => handleDelete(crop.id)} vertical={false} align="center" gap="0.5rem">
          <Icon className="red-text" dataTestId={`${dataTestId}-top-crop-delete-icon-button`} iconName="tabler/trash" />
          <p className="paragraph-m-bold red-text margin-0 ">{t("cropExplorer:addEditCrop.delete")}</p>
        </Flex>
      ) : null}
      <Flex justify="flex-end">
        <Button
          dataTestId="crop-explorer-save-button"
          className="crop-explorer save-button"
          type="primary"
          size="small"
          htmlType="submit"
          disabled={isPendingAddCropExplorationEntry || isPendingUpdateCropExplorationEntry || isDeletionPending}
          loading={isPendingAddCropExplorationEntry || isPendingUpdateCropExplorationEntry || isDeletionPending}
        >
          {t("addEditCrop.save")}
        </Button>
      </Flex>
    </Form>
  );
};

export const generateCropSelectionGroups = ({
  cropExploration,
  crops,
  cropType,
  isAdmin,
  selectedCropId,
  t,
  year,
}: {
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse;
  crops: ExtendedCrop[];
  cropType: CROP_TYPE;
  isAdmin: boolean;
  selectedCropId?: string;
  t: (key: string) => string;
  year: number;
}) => {
  // This is purely to let TS know that all crops returned from a .filter are not undefined
  const isDefinedCrop = (crop: ExtendedCrop | undefined): crop is ExtendedCrop => !!crop;

  const isBauYear = cropExploration.bauYears.includes(year);
  const cropExplorationKey = isBauYear ? "bauEntries" : "yearlyEntries";
  const thisYearsEntries = cropExploration[cropExplorationKey].find((entries) => entries.year === year);
  const selectableCrops = crops.filter((crop: ExtendedCrop) => crop.id !== thisYearsEntries?.main?.cropId);

  // If a side crop is dragged to the main column, we need to display it again in the side crop columns
  const sideCropSelectedAsMainCrop = crops.find((crop) => crop.id === thisYearsEntries?.main?.cropId);
  const selectedCrop = crops.find((crop) => crop.id === selectedCropId);

  const selectableMainCrops = [selectedCrop, ...selectableCrops.filter((crop) => crop.isMainCrop)].filter(isDefinedCrop);
  const selectableSideCrops = [
    ...[sideCropSelectedAsMainCrop, ...selectableCrops]
      .filter(isDefinedCrop)
      .filter((crop) => !(thisYearsEntries?.side || []).map((entry) => entry.cropId).includes(crop.id!)),
    selectedCrop,
  ].filter(isDefinedCrop);

  const selectableCatchCrops = selectableSideCrops.filter((crop) => crop.isCatchCrop);
  const selectableUndersowingCrops = selectableSideCrops.filter((crop) => crop.isUndersowing);

  if (cropType === CROP_TYPE.MAIN) {
    return (
      <>
        {!isAdmin && (
          <OptGroup
            data-testid="crop-creation-form-crop-selection-group-most-common"
            key="crop-selection-group-most-common"
            label={t("modules:bauEntryDrawer.crops.mostCommon")}
          >
            {selectableMainCrops
              .filter((crop) => crop.isTopCrop)
              .map((option, index) => createOption({ index: index, option: option, groupName: "most-common" }))}
          </OptGroup>
        )}
        <OptGroup data-testid="crop-creation-form-crop-selection-group-a-z" key="crop-selection-group-a-z" label={t("modules:bauEntryDrawer.crops.allCrops")}>
          {selectableMainCrops
            .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
            .map((option, index) => createOption({ index: index, option: option, groupName: "a-z" }))}
        </OptGroup>
      </>
    );
  }
  if (cropType === CROP_TYPE.SIDE) {
    return (
      <>
        <OptGroup
          data-testid="crop-creation-form-crop-selection-group-catch-crop"
          key="crop-selection-group-catch-crop"
          label={t("modules:bauEntryDrawer.crops.catchCrop")}
        >
          {selectableCatchCrops
            .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
            .map((option, index) => createOption({ index: index, option: option, groupName: "catch-crop" }))}
        </OptGroup>
        <OptGroup
          data-testid="crop-creation-form-crop-selection-group-undersowing"
          key="crop-selection-group-undersowing"
          label={t("modules:bauEntryDrawer.crops.undersowing")}
        >
          {selectableUndersowingCrops
            .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
            .map((option, index) => createOption({ index: index, option: option, groupName: "undersowing" }))}
        </OptGroup>
        <OptGroup data-testid="crop-creation-form-crop-selection-group-other" key="crop-selection-group-other" label={t("modules:bauEntryDrawer.crops.other")}>
          {selectableSideCrops
            .filter((crop) => !crop.isCatchCrop && !crop.isUndersowing)
            .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
            .map((option, index) => createOption({ index: index, option: option, groupName: "other" }))}
        </OptGroup>
      </>
    );
  }
};

const createOption = ({ index, option, groupName }: { index: number; option: WithInjectedName<PracticeCrop>; groupName: string }) => (
  <Option
    data-testid={`crop-creation-form-crop-selection-${groupName}-option-${index}`}
    key={`${groupName}-${option.id}`}
    label={option.synonyms.length > 0 ? option.synonyms : [option.name]}
    value={option.id}
  >
    {option.name}
  </Option>
);
